import { supabaseClient } from "@/lib/supabase";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface UpdateOrganisationLogoParams {
	organisationId: string;
	file: File;
}

interface UpdateOrganisationLogoResult {
	organisationId: string;
	logo_storage_path: string;
	signedUrl: string | undefined;
}

async function updateOrganisationLogo({
	organisationId,
	file,
}: UpdateOrganisationLogoParams): Promise<UpdateOrganisationLogoResult> {
	const fileExt = file.name.split(".").pop();
	// Use a consistent filename without timestamp
	const storagePath = `${organisationId}.${fileExt}`;

	// Upload to storage with cache control
	const { error: uploadError } = await supabaseClient.storage
		.from("organisation-logos")
		.upload(storagePath, file, {
			upsert: true,
			cacheControl: "no-cache", // Prevent caching
		});

	if (uploadError) throw uploadError;

	// Update organisation with storage path
	const { error: updateError } = await supabaseClient
		.from("organisations")
		.update({ logo_storage_path: storagePath })
		.eq("organisation_id", organisationId);

	if (updateError) throw updateError;

	// Get a signed URL for the uploaded file
	const { data: signedUrlData } = await supabaseClient.storage
		.from("organisation-logos")
		.createSignedUrl(storagePath, 60, {
			transform: {
				quality: 75, // Optional: Optimize image quality
			},
		});

	return {
		organisationId,
		logo_storage_path: storagePath,
		signedUrl: signedUrlData?.signedUrl,
	};
}

export function useUpdateOrganisationLogo() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateOrganisationLogo,
		onSuccess: () => {
			// Invalidate and refetch organisations query
			queryClient.invalidateQueries({ queryKey: ["organisations"] });
		},
	});
}
